/**
 * DAMM V2 Position Fee Reader
 *
 * Reads claimable fees from DAMM V2 locked positions using Q128 checkpoint math.
 * Post-graduation, creator fees accrue to permanently locked positions (70% creator, 30% treasury).
 *
 * Formula: claimable = (pool.feePerLiquidity - position.feePerTokenCheckpoint) * totalLiquidity >> 128
 *
 * The fee accumulator uses Q128 fixed-point precision (NOT Q64).
 */

import { Connection, PublicKey } from '@solana/web3.js';
import BN from 'bn.js';
import {
  createDammV2Program,
  DAMM_V2_PROGRAM_ID,
} from '@meteora-ag/dynamic-bonding-curve-sdk';
import { getSolanaWsEndpoint } from '@/lib/solana/config';

const DEBUG = import.meta.env.DEV;

import {
  CREATOR_SHARE_BPS_GRADUATED,
  BPS_DENOMINATOR as BPS_DENOMINATOR_NUMBER,
} from '@/lib/constants/feeShares';

// Q128 resolution for fee-per-liquidity fixed-point math
const Q128 = BigInt(1) << BigInt(128);

// Creator share is centralised in feeShares.ts (CREATOR_SHARE_BPS_GRADUATED = 7000).
const DAMM_V2_CREATOR_SHARE_BPS = BigInt(CREATOR_SHARE_BPS_GRADUATED);
const BPS_DENOMINATOR = BigInt(BPS_DENOMINATOR_NUMBER);

export interface DammV2PositionFees {
  poolAddress: string;
  positionAddress: string;
  nftMint: string;
  claimableFeeA: BN; // token lamports
  claimableFeeB: BN; // SOL lamports
  totalLiquidity: BN;
  totalLifetimeFeeB: BN; // Creator's 70% of pool's cumulative totalLpBFee (never decreases)
  // Pool state needed for claim tx
  tokenAMint: string;
  tokenBMint: string;
  tokenAVault: string;
  tokenBVault: string;
}

interface DecodedPool {
  feeBPerLiquidity: number[];
  feeAPerLiquidity: number[];
  tokenAMint: PublicKey;
  tokenBMint: PublicKey;
  tokenAVault: PublicKey;
  tokenBVault: PublicKey;
  metrics: {
    totalLpBFee: BN; // Cumulative total LP SOL fees (u128, never decreases)
  };
}

interface DecodedPosition {
  pool: PublicKey;
  feeBPerTokenCheckpoint: number[];
  feeAPerTokenCheckpoint: number[];
  feeBPending: BN;
  feeAPending: BN;
  permanentLockedLiquidity: BN;
  unlockedLiquidity: BN;
  vestedLiquidity: BN;
  nftMint: PublicKey;
}

/**
 * Convert a LE u8 array (up to 32 bytes / u256) to BigInt
 */
function u8ArrayToBigInt(arr: number[] | Uint8Array): bigint {
  let result = BigInt(0);
  for (let i = Math.min(arr.length, 32) - 1; i >= 0; i--) {
    result = result * BigInt(256) + BigInt(arr[i] || 0);
  }
  return result;
}

/**
 * Convert a BN to BigInt
 */
function bnToBigInt(bn: BN | number | undefined): bigint {
  if (!bn) return BigInt(0);
  return BigInt(bn.toString());
}

/**
 * Convert BigInt lamports to BN for UI compatibility
 */
function bigIntToBN(value: bigint): BN {
  return new BN(value.toString());
}

/**
 * Compute claimable fee from Q128 checkpoint math.
 * claimable = (poolFeePerLiq - posCheckpoint) * totalLiquidity >> 128 + pending
 */
function computeClaimableFee(
  poolFeePerLiquidity: number[],
  posCheckpoint: number[],
  totalLiquidity: bigint,
  pending: bigint
): bigint {
  const poolFee = u8ArrayToBigInt(poolFeePerLiquidity);
  const posCheck = u8ArrayToBigInt(posCheckpoint);

  if (poolFee <= posCheck) return pending;

  const delta = poolFee - posCheck;
  const claimable = (delta * totalLiquidity) / Q128;
  return claimable + pending;
}

/**
 * Fetch claimable fees for DAMM V2 positions.
 * Reads pool + position accounts in a single batched RPC call.
 */
export async function fetchDammV2PositionFees(
  connection: Connection,
  poolAddress: PublicKey,
  positionAddresses: PublicKey[]
): Promise<DammV2PositionFees[]> {
  if (positionAddresses.length === 0) return [];

  const allAddresses = [poolAddress, ...positionAddresses];

  DEBUG && console.log(
    '[DammV2FeeReader] Fetching pool + positions:',
    allAddresses.map(a => a.toBase58().slice(0, 8)).join(', ')
  );

  const accountInfos = await connection.getMultipleAccountsInfo(allAddresses);

  // Get the DAMM V2 Anchor program for decoding
  const dammProgram = createDammV2Program(connection);

  // Decode pool (first account)
  const poolInfo = accountInfos[0];
  if (!poolInfo?.data) {
    console.warn('[DammV2FeeReader] Pool account not found:', poolAddress.toBase58());
    return [];
  }

  let pool: DecodedPool;
  try {
    pool = dammProgram.coder.accounts.decode('pool', poolInfo.data) as DecodedPool;
  } catch (e) {
    console.error('[DammV2FeeReader] Failed to decode pool:', e);
    return [];
  }

  // Creator's lifetime SOL fees = totalLpBFee * 70%
  const totalLpBFee = bnToBigInt(pool.metrics?.totalLpBFee);
  const creatorLifetimeFeeB = (totalLpBFee * DAMM_V2_CREATOR_SHARE_BPS) / BPS_DENOMINATOR;

  DEBUG && console.log(
    '[DammV2FeeReader] Pool totalLpBFee:',
    (Number(totalLpBFee) / 1e9).toFixed(6), 'SOL,',
    'creator 70%:', (Number(creatorLifetimeFeeB) / 1e9).toFixed(6), 'SOL'
  );

  // Decode each position and compute fees
  const results: DammV2PositionFees[] = [];

  for (let i = 0; i < positionAddresses.length; i++) {
    const posInfo = accountInfos[i + 1];
    if (!posInfo?.data) {
      DEBUG && console.log('[DammV2FeeReader] Position not found:', positionAddresses[i].toBase58());
      continue;
    }

    try {
      const pos = dammProgram.coder.accounts.decode('position', posInfo.data) as DecodedPosition;

      const totalLiquidity =
        bnToBigInt(pos.permanentLockedLiquidity) +
        bnToBigInt(pos.unlockedLiquidity) +
        bnToBigInt(pos.vestedLiquidity);

      const claimableFeeA = computeClaimableFee(
        pool.feeAPerLiquidity,
        pos.feeAPerTokenCheckpoint,
        totalLiquidity,
        bnToBigInt(pos.feeAPending)
      );

      const claimableFeeB = computeClaimableFee(
        pool.feeBPerLiquidity,
        pos.feeBPerTokenCheckpoint,
        totalLiquidity,
        bnToBigInt(pos.feeBPending)
      );

      results.push({
        poolAddress: poolAddress.toBase58(),
        positionAddress: positionAddresses[i].toBase58(),
        nftMint: pos.nftMint.toBase58(),
        claimableFeeA: bigIntToBN(claimableFeeA),
        claimableFeeB: bigIntToBN(claimableFeeB),
        totalLiquidity: bigIntToBN(totalLiquidity),
        totalLifetimeFeeB: bigIntToBN(creatorLifetimeFeeB),
        tokenAMint: pool.tokenAMint.toBase58(),
        tokenBMint: pool.tokenBMint.toBase58(),
        tokenAVault: pool.tokenAVault.toBase58(),
        tokenBVault: pool.tokenBVault.toBase58(),
      });

      DEBUG && console.log(
        '[DammV2FeeReader] Position', positionAddresses[i].toBase58().slice(0, 8),
        'claimable SOL:', (Number(claimableFeeB) / 1e9).toFixed(6)
      );
    } catch (e) {
      console.error('[DammV2FeeReader] Failed to decode position:', positionAddresses[i].toBase58(), e);
    }
  }

  return results;
}
